import {
  actorName,
  isTaskStatus,
  payloadText,
  type Sentence,
  type Translate,
} from "@/features/collaboration/lib/sentence";
import { peopleSentence } from "@/features/notifications/lib/peopleSentences";
import type { Schemas } from "@/shared/api/client";
import type { Locale } from "@/shared/i18n/messages";

export type Notification = Schemas["NotificationOut"];

const PROJECT_ROLES = ["lead", "contributor", "viewer"];

/** The task's reference, or its title, or the project's name: whatever the payload still has. */
function taskTarget(notification: Notification): Pick<Sentence, "targetKind"> & {
  target: string;
} {
  const payload = notification.payload;
  const reference = payloadText(payload, "reference");
  if (reference !== null) return { target: reference, targetKind: "reference" };
  const name = payloadText(payload, "title") ?? payloadText(payload, "project_name") ?? "";
  return { target: name, targetKind: "name" };
}

/**
 * A notification as a sentence about you: "Mariam Fathy assigned you NOON-12", "أحمد سمير ذكرك
 * في تعليق على BQ-9", "Nour Hassan moved BQ-9 to In review".
 */
export function notificationSentence(
  notification: Notification,
  translate: Translate,
  locale: Locale = "ar",
): Sentence {
  const people = peopleSentence(notification, translate, locale);
  if (people) return people;
  const actor = actorName(notification.actor, translate);
  const { target, targetKind } = taskTarget(notification);
  const values = { actor, target };
  switch (notification.kind) {
    case "assigned":
      return { id: "notifications.sentence.assigned", values, targetKind };
    case "mentioned":
      return { id: "notifications.sentence.mentioned", values, targetKind };
    case "commented":
      return { id: "notifications.sentence.commented", values, targetKind };
    case "status_changed": {
      const to = notification.payload.to;
      return isTaskStatus(to)
        ? {
            id: "notifications.sentence.statusChanged",
            values: { ...values, status: translate(`board.status.${to}`) },
            targetKind,
          }
        : { id: "notifications.sentence.updated", values, targetKind };
    }
    case "member_added": {
      const role = payloadText(notification.payload, "role");
      const project = payloadText(notification.payload, "project_name") ?? target;
      return {
        id: "notifications.sentence.memberAdded",
        values: {
          actor,
          target: project,
          role: role && PROJECT_ROLES.includes(role) ? role : "other",
        },
        targetKind: "name",
      };
    }
    case "approval_approved":
    case "approval_changes_requested": {
      // The client decided; the payload names them and the approval's title.
      const by = payloadText(notification.payload, "by") ?? actor;
      return {
        id:
          notification.kind === "approval_approved"
            ? "notifications.sentence.approvalApproved"
            : "notifications.sentence.approvalChangesRequested",
        values: { actor: by, target: payloadText(notification.payload, "title") ?? target },
        targetKind: "name",
      };
    }
    case "unblocked": {
      // The last open task this one waited for is done; the payload names it.
      const blocker = payloadText(notification.payload, "blocker_reference");
      return blocker === null
        ? { id: "notifications.sentence.updated", values, targetKind }
        : { id: "notifications.sentence.unblocked", values: { ...values, blocker }, targetKind };
    }
    case "ticket_opened":
      return {
        id: "notifications.sentence.ticketOpened",
        values: { actor, target: payloadText(notification.payload, "number") ?? target },
        targetKind: "reference",
      };
    case "ticket_replied":
      return {
        id: "notifications.sentence.ticketReplied",
        values: { actor, target: payloadText(notification.payload, "number") ?? target },
        targetKind: "reference",
      };
    default:
      return { id: "notifications.sentence.updated", values, targetKind };
  }
}

/** The quoted words of a mention or comment, already in readable "@Name" form. */
export function notificationExcerpt(notification: Notification): string | null {
  return notification.kind === "mentioned" ||
    notification.kind === "commented" ||
    notification.kind === "approval_changes_requested"
    ? payloadText(notification.payload, "excerpt")
    : null;
}

/** The task title under the sentence, when the sentence names the task by its reference. */
export function notificationDetail(notification: Notification): string | null {
  if (notification.kind === "member_added") return null;
  if (notification.kind === "ticket_opened" || notification.kind === "ticket_replied") {
    return payloadText(notification.payload, "title");
  }
  return payloadText(notification.payload, "reference") === null
    ? null
    : payloadText(notification.payload, "title");
}

/** Where a notification leads: its task over the project's board, the project, or a ticket. */
export function notificationDestination(notification: Notification): {
  projectKey: string | null;
  reference: string | null;
  ticketId: string | null;
} {
  const payload = notification.payload;
  if (notification.kind === "ticket_opened" || notification.kind === "ticket_replied") {
    return {
      projectKey: null,
      reference: null,
      ticketId: payloadText(payload, "ticket_id"),
    };
  }
  return {
    projectKey: payloadText(payload, "project_key"),
    reference: notification.task_id === null ? null : payloadText(payload, "reference"),
    ticketId: null,
  };
}
